import { Link, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { animeList, siteInfo } from '@/mocks/anime';

export default function Article() {
  const { id } = useParams();

  const anime = useMemo(() => {
    const numId = parseInt(id || '0', 10);
    return animeList.find((a) => a.id === numId) || null;
  }, [id]);

  const relatedAnime = useMemo(() => {
    if (!anime) return [];
    return animeList
      .filter(
        (a) =>
          a.id !== anime.id &&
          a.tags.some((t) => anime.tags.includes(t))
      )
      .slice(0, 4);
  }, [anime]);

  if (!anime) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">
              作品未找到
            </h1>
            <p className="text-foreground-500 mb-6">
              抱歉，您要查看的动漫作品不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: window.location.origin,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '分类浏览',
                  item: `${window.location.origin}/category/all`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: anime.title,
                },
              ],
            },
            {
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: `${anime.title} - 樱花动漫`,
              description: anime.description,
              image: anime.image,
              url: window.location.href,
              datePublished: `${anime.year}-01-01`,
              author: {
                '@type': 'Organization',
                name: siteInfo.name,
              },
              publisher: {
                '@type': 'Organization',
                name: siteInfo.name,
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: `${anime.title}是什么类型的动漫？`,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: `${anime.title}是一部${anime.tags.join('、')}类型的动漫作品，共${anime.episodes}集，于${anime.year}年播出。`,
                  },
                },
                {
                  '@type': 'Question',
                  name: `${anime.title}好看吗？评分多少？`,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: `${anime.title}在樱花动漫网的评分为${anime.rating}分，是一部非常值得观看的动漫作品。`,
                  },
                },
              ],
            },
          ]),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category/all' },
              { label: anime.title },
            ]}
          />

          {/* Article Header */}
          <article className="mt-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Poster */}
              <div className="flex-shrink-0 w-full lg:w-72">
                <div className="aspect-[2/3] rounded-xl overflow-hidden">
                  <img
                    src={anime.image}
                    alt={anime.title}
                    title={`${anime.title} - 樱花动漫推荐`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {anime.title}
                </h1>

                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-sm font-medium">
                    <i className="ri-star-fill text-accent-500"></i>
                    {anime.rating} 分
                  </span>
                  <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-sm">
                    {anime.status}
                  </span>
                  <span className="text-sm text-foreground-500">
                    {anime.episodes}集 · {anime.year}年
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap mb-5">
                  {anime.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${anime.category}`}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
                  <p>{anime.description}</p>
                </div>

                <div className="mt-6 flex items-center gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                作品介绍
              </h2>
              <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed space-y-4">
                <p>
                  欢迎来到樱花动漫网！{anime.title}是我们精心挑选推荐给动漫爱好者的优秀作品。
                </p>
                <p>
                  {anime.title}是一部{anime.year}
                  年播出的{anime.tags.join('、')}
                  类动漫，共{anime.episodes}集。这部作品在樱花动漫网上获得了{' '}
                  <strong>{anime.rating}</strong>{' '}
                  的高分评价，深受动漫爱好者的喜爱。
                </p>
                <h3>剧情简介</h3>
                <p>{anime.description}</p>
                <h3>推荐理由</h3>
                <p>
                  如果你喜欢{anime.tags.join('、')}
                  类型的作品，那么{anime.title}
                  绝对不容错过。它不仅在角色塑造上极为用心，剧情节奏也把握得恰到好处。无论你是资深动漫迷还是刚入坑的新人，都能在这部作品中找到属于自己的乐趣。
                </p>
                <p>
                  在樱花动漫网，我们持续为动漫爱好者推荐更多好看的动漫作品。点击下方相关推荐，发现更多精彩内容吧！
                </p>
              </div>
            </div>
          </article>

          {/* Related Anime */}
          {relatedAnime.length > 0 && (
            <section className="mt-12">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-product-shop="">
                {relatedAnime.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                          {item.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">
                      {item.status} · {item.episodes}集
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}